import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  Layout,
  DataTable,
  Badge,
  Button,
  Select
} from '@shopify/polaris';
import { openprojectService } from '../services/openprojectService';
import { personnelService } from '../services/personnelService';
import { useAuth } from '../contexts/AuthContext';
import LoadingState from './shared/LoadingState';
import PageError from './shared/PageError';

const Users = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [users, setUsers] = useState([]);
  const [managerAssignments, setManagerAssignments] = useState({}); // openProjectUserId -> managerId
  const [availableManagers, setAvailableManagers] = useState([]);
  const [savingFor, setSavingFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      setError('');
      setLoading(true);
      const users = await openprojectService.getUsers();
      setUsers(users);

      if (isAdmin) {
        const managerData = await personnelService.getManagers();
        setManagerAssignments(
          Object.fromEntries((managerData.personnel || []).map((p) => [p.openProjectUserId, p.managerId || '']))
        );
        setAvailableManagers(managerData.availableManagers || []);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignManager = async (openProjectUserId, managerId) => {
    setSavingFor(openProjectUserId);
    setManagerAssignments((prev) => ({ ...prev, [openProjectUserId]: managerId }));
    try {
      await personnelService.assignManager(openProjectUserId, managerId || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save manager assignment');
    } finally {
      setSavingFor(null);
    }
  };

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`);
  };

  if (loading) {
    return (
      <Page title="Users">
        <LoadingState />
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Users" primaryAction={{ content: 'Retry', onAction: loadUsers }}>
        <PageError>{error}</PageError>
      </Page>
    );
  }

  const managerOptions = [
    { label: 'No manager', value: '' },
    ...availableManagers.map((m) => ({ label: m.name, value: m.id }))
  ];

  const userRows = users.map(user => {
    const row = [
      user.name || 'Unknown',
      user.email || 'No email',
      user.login || 'No login',
      <Badge tone={user.status === 'active' ? 'success' : 'warning'}>
        {user.status || 'Unknown'}
      </Badge>,
      user.admin ? 'Yes' : 'No',
      user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US') : 'N/A',
      <Button
        size="slim"
        onClick={() => handleUserClick(user.id)}
      >
        View Details
      </Button>
    ];

    if (isAdmin) {
      const id = user.id.toString();
      row.splice(6, 0,
        <Select
          label="Manager"
          labelHidden
          options={managerOptions.filter((o) => o.value !== id)}
          value={managerAssignments[id] || ''}
          onChange={(value) => handleAssignManager(id, value)}
          disabled={savingFor === id}
        />
      );
    }

    return row;
  });

  const headings = ['Name', 'Email', 'Login', 'Status', 'Admin', 'Created'];
  if (isAdmin) headings.push('Manager');
  headings.push('Actions');

  return (
    <Page
      title="Users"
      subtitle={`Total users: ${users.length}`}
      primaryAction={{
        content: 'Refresh',
        onAction: loadUsers
      }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <DataTable
              columnContentTypes={headings.map(() => 'text')}
              headings={headings}
              rows={userRows}
            />
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Users;
